import { createContext, useContext, useEffect, useState, useRef, type ReactNode } from 'react';
import { onAuthStateChanged, signInWithCustomToken, getIdTokenResult, type User } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from './firebase';
import { establishSession, bindSessionRefresh } from './api';

export interface LabOSUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  role: string;
  isAdmin: boolean;
}

interface LabOSState {
  user: LabOSUser | null;
  firebaseUser: User | null;
  config: Record<string, unknown>;
  loading: boolean;
  signOut: () => Promise<void>;
}

const Ctx = createContext<LabOSState | null>(null);
const LABOS_URL = 'https://labos.wbdcontentlab.dev';

export function LabOSAuth({ children, appId, fallback }: { children: ReactNode; appId: string; fallback?: ReactNode }) {
  const [state, setState] = useState<Omit<LabOSState, 'signOut'>>({
    user: null,
    firebaseUser: null,
    config: {},
    loading: true,
  });
  const isHandshakeCodeProccessing = useRef(false);

  useEffect(() => {
    // Check for handshake code in URL (redirect from LabOS)
    const params = new URLSearchParams(window.location.search);
    const handshakeCode = params.get('handshake');

    if (handshakeCode) {
      // Redeem the handshake code for a Firebase custom token
      const redeem = httpsCallable<{ code: string }, { customToken: string }>(functions, 'redeemHandshakeCode');
      isHandshakeCodeProccessing.current = true;
      redeem({ code: handshakeCode })
        .then(async (result) => {
          await signInWithCustomToken(auth, result.data.customToken);
        })
        .catch((err) => {
          console.error('[LabOS] Handshake redemption failed:', err);
        })
        .finally(() => {
          isHandshakeCodeProccessing.current = false;
          // Clean up URL
          params.delete('handshake');
          params.delete('origin');
          const clean = window.location.pathname + (params.toString() ? '?' + params : '');
          window.location.href = clean;
        });
    }

    return onAuthStateChanged(auth, async (fbUser) => {
      if (isHandshakeCodeProccessing.current) return;
      if (!fbUser) {
        // Not authenticated — redirect to LabOS
        const returnUrl = encodeURIComponent(window.location.href);
        window.location.href = `${LABOS_URL}/login?return=${returnUrl}&app=${appId}`;
        return;
      }

      const token = await getIdTokenResult(fbUser);
      const role = (token.claims.role as string) || 'user';

      // Establish the __session cookie BEFORE rendering children (they mount
      // <img>/<video> asset requests that authenticate via that cookie), and
      // keep it refreshed as the ID token rotates.
      await establishSession();
      bindSessionRefresh();

      // Fetch runtime config from LabOS (group-level app parameters)
      let config: Record<string, unknown> = {};
      try {
        const getAppConfig = httpsCallable<{ appId: string }, Record<string, unknown>>(functions, 'getAppConfig');
        const result = await getAppConfig({ appId });
        config = result.data || {};
      } catch {
        /* config is optional */
      }

      setState({
        user: {
          uid: fbUser.uid,
          email: fbUser.email,
          displayName: fbUser.displayName,
          role,
          isAdmin: role === 'admin',
        },
        firebaseUser: fbUser,
        config,
        loading: false,
      });
    });
  }, [appId]);

  const signOut = async () => {
    await auth.signOut();
    window.location.href = LABOS_URL;
  };

  if (state.loading) {
    return (
      <>
        {fallback || (
          <div
            style={{
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#fff',
              color: 'rgba(0,0,0,0.3)',
              fontSize: '14px',
              fontFamily: 'system-ui',
            }}
          >
            Authenticating...
          </div>
        )}
      </>
    );
  }

  return <Ctx.Provider value={{ ...state, signOut }}>{children}</Ctx.Provider>;
}

// Simple mock for development - provides context without errors
export function LabOSAuthMock({ children }: { children: ReactNode }) {
  const mockState: LabOSState = {
    user: null,
    firebaseUser: null,
    config: {},
    loading: false,
    signOut: async () => {
      console.log('[LabOS Mock] Sign out called');
    },
  };

  return <Ctx.Provider value={mockState}>{children}</Ctx.Provider>;
}

export function useLabOS(): LabOSState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useLabOS() must be inside <LabOSAuth>');
  return ctx;
}

/**
 * The shared LabOS strip. Themed with the app's own tokens rather than the hardcoded
 * white it used to be, so it darkens with the rest of Story Engine instead of
 * sitting as a bright band above a dark app.
 *
 * `actions` lets the host app mount its own controls (theme, language) in the strip.
 */
export function LabOSTopbar({ appName, actions }: { appName: string; actions?: ReactNode }) {
  const { user, signOut } = useLabOS();
  return (
    <div
      className={
        'sticky top-0 z-[9999] flex h-10 flex-shrink-0 items-center justify-between ' +
        'border-b border-border bg-elevated px-4 text-xs'
      }
    >
      <div className="flex items-center gap-2">
        <a href={LABOS_URL} className="font-medium tracking-wider text-faint transition-colors hover:text-foreground">
          LabOS
        </a>
        <span className="text-faint/50">/</span>
        <span className="font-semibold text-primary">{appName}</span>
      </div>
      <div className="flex items-center gap-3">
        {actions}
        <span className="hidden text-faint sm:inline">{user?.email}</span>
        <button
          onClick={signOut}
          className={
            'rounded-md border border-border bg-card px-2.5 py-1 text-[11px] text-muted-foreground ' +
            'transition-colors hover:border-faint hover:text-foreground'
          }
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
